import { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { useProfile } from '../context/ProfileContext'
import { useDialog } from '../context/DialogContext'
import { useNavigate } from 'react-router-dom'

export function DevTopBar() {
  const { user, logout } = useAuth()
  const { currentProfile, setProfile, canAccessProfile } = useProfile()
  const { showWarning } = useDialog()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)

  // Apenas mostrar em development e se for master
  if (import.meta.env.PROD || !user?.user_master) {
    return null
  }

  const profiles = [
    {
      id: 'master',
      label: '👑 Master',
      color: 'bg-red-500',
      route: '/master/dashboard'
    },
    {
      id: 'freight-forwarder',
      label: '🚢 Agente de Cargas',
      color: 'bg-blue-500',
      route: '/freight-forwarder/dashboard'
    },
    {
      id: 'shipper',
      label: '📦 Embarcador',
      color: 'bg-green-500',
      route: '/shipper/dashboard'
    },
  ]

  const currentProfileData = profiles.find(p => p.id === currentProfile) || profiles[2]

  const handleProfileSwitch = (profile: typeof profiles[0]) => {
    const profileType = profile.id as 'master' | 'shipper' | 'freight-forwarder'

    if (!canAccessProfile(profileType)) {
      showWarning('Acesso Negado', `Você não tem permissão para acessar "${profile.label}".`)
      setIsOpen(false)
      return
    }

    // Atualiza o profile context
    setProfile(profileType)

    // Navega para a rota correta
    navigate(profile.route)

    setIsOpen(false)
  }

  return (
    <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg fixed top-0 left-0 right-0 z-[60]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-10 text-sm">
          {/* Left side - Dev mode indicator */}
          <div className="flex items-center gap-3">
            <span className="font-mono font-bold">🔧 DEV MODE</span>
            <span className="text-white/70">|</span>
            <span className="text-white/90">
              Logado como: <strong>{user.name}</strong>
            </span>
          </div>

          {/* Center - Profile Switcher */}
          <div className="relative">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-full ${currentProfileData.color} hover:opacity-90 transition-opacity font-medium`}
            >
              <span>{currentProfileData.label}</span>
              <svg
                className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown */}
            {isOpen && (
              <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-xl py-2 min-w-[200px] border border-gray-200">
                {profiles.map((profile) => (
                  <button
                    key={profile.id}
                    onClick={() => handleProfileSwitch(profile)}
                    className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                      currentProfile === profile.id ? 'bg-gray-100 font-semibold' : 'text-gray-700'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${profile.color}`}></span>
                    <span className="text-sm">{profile.label}</span>
                    {currentProfile === profile.id && (
                      <svg className="w-4 h-4 ml-auto text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right side - Quick actions */}
          <div className="flex items-center gap-3">
            <a
              href={currentProfileData.route}
              className="text-white/90 hover:text-white transition-colors"
            >
              Dashboard
            </a>
            <span className="text-white/50">|</span>
            <a
              href={currentProfile === 'shipper' ? '/shipper/quotes' : '#'}
              className="text-white/90 hover:text-white transition-colors"
            >
              {currentProfile === 'shipper' ? 'Cotações' : 'Oportunidades'}
            </a>
            <span className="text-white/50">|</span>
            <button
              onClick={() => logout()}
              className="text-white/90 hover:text-white transition-colors flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sair
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
